import { useState, useEffect } from "react";
import {
    Box, TextField, Typography, Button, Snackbar, Alert,
    CircularProgress, ThemeProvider, createTheme, IconButton,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { makeGetRequestToUrl } from "../commonFunc.jsx";
import config from "../../config/config.js";

// TODO: move to config.js (same token used on the registration page)
const BUSINESS_TOKEN = "ZFOGFQKCi1X8GxjkC4RV2O04dAeT19zO";
const MAX_FILE_MB = 5;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const c = {
    asphalt: "#121519", asphaltDeep: "#0A0C0F", page: "#F2F3F0", surface: "#FFFFFF",
    line: "#E1E4E8", lineStrong: "#C3C8CF", ink: "#15191F", inkSoft: "#5E6672",
    amber: "#F2B33D", amberDeep: "#B9831C", amberSoft: "#FEF5E1",
    go: "#2FA866", goSoft: "#E6F5EC", error: "#C2413B", errorSoft: "#FBECEB",
};

const theme = createTheme({
    palette: { primary: { main: c.asphalt }, error: { main: c.error } },
    typography: { fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif' },
    shape: { borderRadius: 6 },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: c.surface, fontSize: "15px",
                    "& fieldset": { borderColor: c.line },
                    "&:hover fieldset": { borderColor: c.lineStrong },
                    "&.Mui-focused fieldset": { borderColor: c.asphalt, borderWidth: "1px" },
                },
                input: { padding: "13px 14px" },
            },
        },
    },
});

const display = '"Archivo", "Helvetica Neue", Arial, sans-serif';
const mono = '"JetBrains Mono", ui-monospace, Menlo, monospace';

// ---- helpers ---------------------------------------------------------------
const slotsFor = (d) => Math.max(1, Number(d.no_of_images) || 1);
const needsNumber = (d) => Number(d.is_doc_number_required) === 1;
const needsExpiry = (d) => Number(d.is_doc_expired) === 1;
const emptyState = (d) => ({
    urls: Array(slotsFor(d)).fill(null),
    uploading: Array(slotsFor(d)).fill(false),
    number: "",
    expiry: "",
    error: "",
});
const isTouched = (st) => st && (st.urls.some(Boolean) || st.number.trim() || st.expiry);
const getDocError = (d, st) => {
    if (st.uploading.some(Boolean)) return "Wait for the upload to finish";
    if (st.urls.some((u) => !u)) return slotsFor(d) > 1 ? `Upload all ${slotsFor(d)} images` : "Upload an image";
    if (needsNumber(d) && !st.number.trim()) return "Enter the document number";
    if (needsExpiry(d) && !st.expiry) return "Enter the expiry date";
    return "";
};
const isDocRejected = (s) => s === 2 || s === 3;

const STATUS_META = {
    1: { label: "Approved", color: c.go, bg: c.goSoft },
    2: { label: "Rejected", color: c.error, bg: c.errorSoft },
    3: { label: "Rejected", color: c.error, bg: c.errorSoft },
    0: { label: "Under review", color: c.amberDeep, bg: c.amberSoft },
};

const StatusPill = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META[0];
    return (
        <Typography sx={{
            fontSize: "11px", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase",
            px: "8px", py: "3px", borderRadius: "4px", color: m.color, bgcolor: m.bg, flexShrink: 0,
        }}>
            {m.label}
        </Typography>
    );
};

// ---- one upload slot -------------------------------------------------------
const Slot = ({ label, url, uploading, onPick, onRemove }) => {
    const id = `slot-${Math.random().toString(36).slice(2)}`;
    if (url) {
        return (
            <Box sx={{ position: "relative", borderRadius: "8px", overflow: "hidden", border: `1px solid ${c.line}`, aspectRatio: "4 / 3" }}>
                <Box component="img" src={url} alt={label} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <IconButton size="small" onClick={onRemove} aria-label={`Remove ${label}`}
                    sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: c.error } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                </IconButton>
            </Box>
        );
    }
    return (
        <Box component="label" htmlFor={id} sx={{
            aspectRatio: "4 / 3", borderRadius: "8px", cursor: uploading ? "default" : "pointer",
            border: `1.5px dashed ${c.lineStrong}`, bgcolor: c.page,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px",
            "&:hover": { borderColor: c.asphalt },
        }}>
            <input id={id} type="file" accept="image/*" hidden disabled={uploading}
                onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
            {uploading ? <CircularProgress size={22} sx={{ color: c.ink }} /> : <CloudUploadOutlinedIcon sx={{ color: c.ink }} />}
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: c.ink }}>
                {uploading ? "Uploading…" : "Upload image"}
            </Typography>
            <Typography sx={{ fontSize: "11.5px", color: c.inkSoft }}>{label} · max {MAX_FILE_MB} MB</Typography>
        </Box>
    );
};

// ---- reupload card (rejected or never-uploaded document) -------------------
const ReuploadCard = ({ docConfig, name, existing, state, onPick, onRemove, onField }) => {
    const rejected = existing && isDocRejected(existing.status);
    const approved = existing && existing.status === 1;
    const prevImages = existing?.images
        ? (Array.isArray(existing.images) ? existing.images : (typeof existing.images === 'string' ? existing.images.split(';;;;') : [existing.images]))
        : [];
    const total = slotsFor(docConfig);
    const slotLabel = (i) => (total === 2 ? (i === 0 ? "Front" : "Back") : total > 1 ? `Image ${i + 1}` : "Document image");
    return (
        <Box sx={{
            border: `1px solid ${state.error || rejected ? c.error : c.line}`,
            borderRadius: "10px", overflow: "hidden", bgcolor: c.surface,
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, bgcolor: "#FAFAF8", borderBottom: `1px solid ${c.line}` }}>
                <Typography sx={{ flex: 1, fontSize: "14.5px", fontWeight: 600, color: c.ink }}>{name}</Typography>
                {existing ? <StatusPill status={existing.status} /> : (
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, textTransform: "uppercase", px: "8px", py: "3px", borderRadius: "4px", bgcolor: c.page, color: c.inkSoft }}>
                        Not uploaded
                    </Typography>
                )}
            </Box>
            <Box sx={{ p: 2 }}>
                {rejected && existing?.reason && (
                    <Box sx={{ p: 1.5, mb: 2, borderRadius: "8px", bgcolor: c.errorSoft, display: "flex", gap: 1 }}>
                        <ErrorOutlineIcon sx={{ fontSize: 18, color: c.error, mt: "1px" }} />
                        <Typography sx={{ fontSize: "13px", color: c.ink }}>
                            <b>Reason:</b> {existing.reason}
                        </Typography>
                    </Box>
                )}
                {approved && prevImages.length > 0 && (
                <Box sx={{ p: 1.5, mb: 2, borderRadius: "8px", bgcolor: c.goSoft, border: `1px solid ${c.go}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: c.go }} />
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: c.ink }}>
                            Approved document
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                        {prevImages.map((src, i) => (
                            <Box key={i} component="a" href={src} target="_blank" rel="noreferrer"
                                sx={{ display: "block", width: 96, height: 72, borderRadius: "6px", overflow: "hidden", border: `1px solid ${c.line}`, bgcolor: c.page }}>
                                <Box component="img" src={src} alt={`${name} ${i + 1}`}
                                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                            </Box>
                        ))}
                    </Box>

                    {(existing.document_number || existing.expiry_date) && (
                        <Typography sx={{ fontFamily: mono, fontSize: "12px", color: c.inkSoft, mt: 1 }}>
                            {[existing.document_number, existing.expiry_date && `Expires ${existing.expiry_date.slice(0, 10)}`]
                                .filter(Boolean).join(" · ")}
                        </Typography>
                    )}

                    <Typography sx={{ fontSize: "12px", color: c.inkSoft, mt: 1 }}>
                        Upload again below to replace it.
                    </Typography>
                </Box>
            )}
                <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" }, alignItems: "flex-start" }}>
    {/* Upload slots (left) */}
    <Box sx={{
        flex: 1, minWidth: 0, width: "100%", display: "grid", gap: 1.5,
        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(auto-fill, minmax(150px, 1fr))" },
    }}>
        {state.urls.map((u, i) => (
            <Slot key={i} label={slotLabel(i)} url={u} uploading={state.uploading[i]}
                onPick={(f) => onPick(i, f)} onRemove={() => onRemove(i)} />
        ))}
    </Box>

    {/* Previously uploaded image (right) */}
    {prevImages.length > 0 && (
        <Box sx={{ width: { xs: "100%", sm: 190 }, flexShrink: 0 }}>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: c.inkSoft, mb: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Currently uploaded
            </Typography>
            <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr" } }}>
                {prevImages.map((src, i) => (
                    <Box key={i} component="a" href={src} target="_blank" rel="noreferrer"
                        sx={{ display: "block", aspectRatio: "4 / 3", borderRadius: "8px", overflow: "hidden", border: `1px solid ${c.line}`, bgcolor: c.page }}>
                        <Box component="img" src={src} alt={`${name} uploaded ${i + 1}`}
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </Box>
                ))}
            </Box>
        </Box>
    )}
</Box>
                {(needsNumber(docConfig) || needsExpiry(docConfig)) && (
                    <Box sx={{ display: "grid", gap: 2, mt: 2, gridTemplateColumns: { xs: "1fr", sm: needsNumber(docConfig) && needsExpiry(docConfig) ? "1fr 1fr" : "1fr" } }}>
                        {needsNumber(docConfig) && (
                            <TextField fullWidth label="Document number" value={state.number}
                                onChange={(e) => onField("number", e.target.value)} />
                        )}
                        {needsExpiry(docConfig) && (
                            <TextField fullWidth type="date" label="Expiry date" InputLabelProps={{ shrink: true }}
                                value={state.expiry} inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                                onChange={(e) => onField("expiry", e.target.value)} />
                        )}
                    </Box>
                )}
                {state.error && <Typography sx={{ color: c.error, fontSize: "12.5px", mt: 1 }}>{state.error}</Typography>}
            </Box>
        </Box>
    );
};

// ---- page ------------------------------------------------------------------
const FleetRegistrationStatus = () => {
    const navigate = useNavigate();
    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);       // data from registration_state
    const [docConfigs, setDocConfigs] = useState({}); // { [document_id]: required-document config }
    const [uploads, setUploads] = useState({});       // { [document_id]: state }
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const toast = (message, severity = "error") => setSnackbar({ open: true, message, severity });

    // ---- look up status ----
    const checkStatus = async () => {
        const e = email.trim();
        if (!e) return setEmailError("Enter your registered email");
        if (!EMAIL_REGEX.test(e)) return setEmailError("This email address looks invalid");
        setEmailError("");
        setLoading(true);
        try {
            const res = await makeGetRequestToUrl(`${serverUrl}/public/fleet/registration_state`, { email: e });
            if (res?.flag !== 200 || !res.data?.fleet) {
                toast(res?.message || "We couldn’t find a registration for this email.");
                return;
            }
            const data = res.data;

            // Uploaded rows only carry document_id, so fetch the city's document config
            // to get names, image counts, number/expiry rules.
            const cfg = {};
            (data.pending_documents || []).forEach((d) => { cfg[d.document_id] = d; });
            try {
                const cityRes = await makeGetRequestToUrl(`${serverUrl}/public/fleet/get_required_documents`, { business_token: BUSINESS_TOKEN });
                const cityRow = (cityRes?.data || []).find((ct) => ct.city_id === data.fleet.city_id);
                (cityRow?.fleet_required_documents || []).forEach((d) => { cfg[d.document_id] = d; });
            } catch (err) {
                console.error("Could not load document config:", err);
            }

            // Initialise upload state for everything the user may need to (re)upload
            const fullReject = data.fleet.status === 2;
const uploadedDocs = data.uploaded_documents || [];
const initial = {};

if (fullReject) {
    // every document the city requires + anything previously uploaded
    const ids = new Set([
        ...Object.keys(cfg),
        ...uploadedDocs.map((d) => String(d.document_id)),
    ]);
    ids.forEach((id) => {
        const prev = uploadedDocs.find((d) => String(d.document_id) === id);
        initial[id] = {
            ...emptyState(cfg[id] || { no_of_images: 1 }),
            number: prev?.document_number || "",   // prefill, user only replaces images
        };
    });
} else {
    (data.pending_documents || []).forEach((d) => {
        initial[d.document_id] = emptyState(cfg[d.document_id] || d);
    });
    uploadedDocs.filter((d) => isDocRejected(d.status)).forEach((d) => {
        initial[d.document_id] = {
            ...emptyState(cfg[d.document_id] || { no_of_images: 1 }),
            number: d.document_number || "",
        };
    });
}

            setDocConfigs(cfg);
            setUploads(initial);
            setResult(data);
        } catch (err) {
            console.error("Error fetching registration status:", err);
            toast(err?.response?.data?.message || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => { setResult(null); setUploads({}); setDocConfigs({}); };

    // ---- upload handlers ----
    const patchDoc = (id, fn) => setUploads((prev) => ({ ...prev, [id]: fn(prev[id]) }));

    const pickFile = async (docId, index, file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) return patchDoc(docId, (s) => ({ ...s, error: "Please choose an image file (JPG, PNG, etc.)" }));
        if (file.size > MAX_FILE_MB * 1024 * 1024) return patchDoc(docId, (s) => ({ ...s, error: `Image must be under ${MAX_FILE_MB} MB` }));

        patchDoc(docId, (s) => {
            const uploading = [...s.uploading]; uploading[index] = true;
            return { ...s, uploading, error: "" };
        });
        try {
            const fd = new FormData();
            fd.append("image", file);
            const { data } = await axios.post(operationsUrl + "/upload_image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            if (!data?.url) throw new Error("No image URL received.");
            patchDoc(docId, (s) => {
                const urls = [...s.urls]; urls[index] = data.url;
                const uploading = [...s.uploading]; uploading[index] = false;
                return { ...s, urls, uploading };
            });
        } catch (err) {
            patchDoc(docId, (s) => {
                const uploading = [...s.uploading]; uploading[index] = false;
                return { ...s, uploading, error: err?.response?.data?.message || "Unable to upload image. Please try again." };
            });
        }
    };

    const removeFile = (docId, index) =>
        patchDoc(docId, (s) => { const urls = [...s.urls]; urls[index] = null; return { ...s, urls }; });

    const setField = (docId, key, value) => patchDoc(docId, (s) => ({ ...s, [key]: value, error: "" }));

    // ---- submit reuploads ----
const submitReupload = async () => {
    if (submitting || !result) return;

    const mandatory = actionIds.filter(
        (id) => prevDoc(id) || Number(docConfigs[id]?.is_required) === 1
    );
    const touched = actionIds.filter((id) => isTouched(uploads[id]));
    const idsToSubmit = [...new Set([...mandatory, ...touched])];

    if (!idsToSubmit.length) return toast("Upload at least one document to continue.");

    // validate everything before sending anything
    let hasError = false;
    const next = { ...uploads };
    idsToSubmit.forEach((id) => {
        const msg = getDocError(docConfigs[id] || { no_of_images: 1 }, uploads[id]);
        if (msg) { next[id] = { ...uploads[id], error: msg }; hasError = true; }
    });
    if (hasError) { setUploads(next); return toast("Please fix the highlighted documents."); }

    setSubmitting(true);
    try {
        const url = operationsUrl + "/public/fleet/update_document";

        // one request per document
        const results = await Promise.allSettled(
            idsToSubmit.map(async (id) => {
                const st = uploads[id];
                const { data: res } = await axios.post(url, {
                    business_token: BUSINESS_TOKEN,
                    fleet_id: result.fleet.fleet_id,
                    document_id: Number(id),
                    images: st.urls.join(';;;;'),
                    document_number: st.number.trim() || undefined,
                    expiry_date: st.expiry || undefined,
                }, { headers: { "Content-Type": "application/json" } });

                if (res?.flag !== 200) throw new Error(res?.message || "Update failed");
                return res;
            })
        );

        const failed = results.filter((r) => r.status === "rejected");
        const okCount = results.length - failed.length;

        if (!failed.length) {
            toast("Documents submitted for verification.", "success");
        } else if (okCount) {
            toast(`${okCount} document(s) submitted, ${failed.length} failed. Please try the failed ones again.`);
        } else {
            const reason = failed[0].reason;
            toast(reason?.response?.data?.message || reason?.message || "We couldn’t submit your documents.");
        }

        // refresh so successful documents move to "under review" and failed ones stay editable
        if (okCount) await checkStatus();
    } finally {
        setSubmitting(false);
    }
};

const fleet = result?.fleet;
const uploaded = result?.uploaded_documents || [];
const pendingDocs = result?.pending_documents || [];
const fullReject = fleet?.status === 2;

const prevDoc = (id) => uploaded.find((d) => String(d.document_id) === String(id));
const rejectedDocs = uploaded.filter((d) => isDocRejected(d.status));
const reviewDocs = uploaded.filter((d) => d.status !== 1 && !isDocRejected(d.status));
const approvedDocs = uploaded.filter((d) => d.status === 1);
// status 0 + edited after creation = was rejected (or edited) and re-uploaded
const isResubmitted = (d) =>
    d.status === 0 &&
    d.updated_at && d.created_at &&
    new Date(d.updated_at) - new Date(d.created_at) > 60 * 1000;

// which documents show an upload card
const actionIds = (fullReject
    ? Object.keys(uploads)
    : [...rejectedDocs, ...pendingDocs].map((d) => String(d.document_id))
).filter((id) => {
    if (!uploads[id]) return false;
    const status = prevDoc(id)?.status;
    if (status === 1) return false;                 // approved: read-only
    if (fullReject && status === 0) return false;   // under review (incl. resubmitted): wait for admin
    return true;
});

const needsAction = actionIds.length > 0;
const docName = (id) => docConfigs[id]?.document_name || `Document #${id}`;

    // ---- views ----
    const lookupView = (
        <Box sx={{ maxWidth: 480, mx: "auto" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 500, color: c.ink, mb: "7px" }}>Registered email</Typography>
            <TextField fullWidth type="email" placeholder="ops@yourfleet.com" autoComplete="email"
                value={email} onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                onKeyDown={(e) => e.key === "Enter" && !loading && checkStatus()}
                error={Boolean(emailError)} helperText={emailError} />
            <Button fullWidth variant="contained" disableElevation disabled={loading} onClick={checkStatus}
                endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: "18px !important", color: c.amber }} />}
                sx={{ mt: 2.5, height: 48, textTransform: "none", fontSize: "15px", fontWeight: 500, bgcolor: c.asphalt, "&:hover": { bgcolor: c.asphaltDeep } }}>
                {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Check status"}
            </Button>
        </Box>
    );

    const summaryHeader = (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
            <Box>
                <Typography sx={{ fontFamily: display, fontSize: "20px", fontWeight: 700, color: c.ink }}>{fleet?.name}</Typography>
                <Typography sx={{ fontSize: "13px", color: c.inkSoft }}>{fleet?.email}</Typography>
            </Box>
            <Button onClick={reset} sx={{ textTransform: "none", color: c.inkSoft }}>Use a different email</Button>
        </Box>
    );

    const approvedView = (
        <Box sx={{ textAlign: "center", py: 2 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: c.goSoft, mx: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 34, color: c.go }} />
            </Box>
            <Typography sx={{ fontFamily: display, fontSize: "22px", fontWeight: 700, color: c.ink, mt: 2 }}>You’re approved</Typography>
            <Typography sx={{ fontSize: "14.5px", color: c.inkSoft, mt: 1, mb: 3 }}>
                Your fleet registration has been verified. You can log in now.
            </Typography>
            <Button variant="contained" disableElevation onClick={() => navigate("/signup")}
                endIcon={<ArrowForwardIcon sx={{ fontSize: "18px !important", color: c.amber }} />}
                sx={{ height: 48, px: 4, textTransform: "none", fontSize: "15px", bgcolor: c.asphalt, "&:hover": { bgcolor: c.asphaltDeep } }}>
                Log in
            </Button>
        </Box>
    );


const readOnlyDocs = [...reviewDocs, ...approvedDocs];

const bannerText = needsAction
    ? fullReject
        ? "Your registration was rejected. Please upload the documents below again. Approved documents stay as they are."
        : "Some of your documents need attention. Please re-upload the rejected ones below."
    : fullReject
        ? "We’ve received your updated documents and are re-verifying them. We’ll update you soon."
        : "Your documents are being verified. We’ll update you soon — check back with your email.";

    const documentsView = (
        <Box>
            {/* Banner */}
            <Box sx={{
                display: "flex", gap: 1.5, p: 2, borderRadius: "10px", mb: 3,
                bgcolor: needsAction ? c.errorSoft : c.amberSoft,
                border: `1px solid ${needsAction ? c.error : c.amber}`,
            }}>
                {needsAction
                    ? <ErrorOutlineIcon sx={{ color: c.error }} />
                    : <HourglassTopIcon sx={{ color: c.amberDeep }} />}
                <Typography sx={{ fontSize: "14px", color: c.ink }}>{bannerText}</Typography>
            </Box>

            {/* Needs action */}
            {needsAction && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                    <Typography sx={{ fontFamily: display, fontSize: "16px", fontWeight: 600, color: c.ink }}>Needs your action</Typography>
                    {/* {rejectedDocs.map((d) => uploads[d.document_id] && (
                        <ReuploadCard key={`r-${d.document_id}`} docConfig={docConfigs[d.document_id] || { no_of_images: 1 }}
                            name={docName(d.document_id)} existing={d} state={uploads[d.document_id]}
                            onPick={(i, f) => pickFile(d.document_id, i, f)} onRemove={(i) => removeFile(d.document_id, i)}
                            onField={(k, v) => setField(d.document_id, k, v)} />
                    ))}
                    {pendingDocs.map((d) => uploads[d.document_id] && (
                        <ReuploadCard key={`p-${d.document_id}`} docConfig={d} name={d.document_name} existing={null}
                            state={uploads[d.document_id]}
                            onPick={(i, f) => pickFile(d.document_id, i, f)} onRemove={(i) => removeFile(d.document_id, i)}
                            onField={(k, v) => setField(d.document_id, k, v)} />
                    ))} */}
                    {actionIds.map((id) => (
                        <ReuploadCard
                            key={id}
                            docConfig={docConfigs[id] || { no_of_images: 1 }}
                            name={docName(id)}
                            existing={prevDoc(id) || null}
                            state={uploads[id]}
                            onPick={(i, f) => pickFile(id, i, f)}
                            onRemove={(i) => removeFile(id, i)}
                            onField={(k, v) => setField(id, k, v)}
                        />
                    ))}
                    <Button variant="contained" disableElevation disabled={submitting} onClick={submitReupload}
                        sx={{ alignSelf: "flex-end", height: 48, px: 4, textTransform: "none", fontSize: "15px", bgcolor: c.asphalt, "&:hover": { bgcolor: c.asphaltDeep } }}>
                        {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit for verification"}
                    </Button>
                </Box>
            )}

            {/* Read-only: under review + approved */}
            {/* {!fullReject && [...reviewDocs, ...approvedDocs].length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Typography sx={{ fontFamily: display, fontSize: "16px", fontWeight: 600, color: c.ink }}>Other documents</Typography>
                    {[...reviewDocs, ...approvedDocs].map((d) => (
                        <Box key={d.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: `1px solid ${c.line}`, borderRadius: "10px" }}>
                            <Box component="img" src={d.images} alt={docName(d.document_id)}
                                sx={{ width: 52, height: 52, borderRadius: "6px", objectFit: "cover", bgcolor: c.page }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: c.ink }}>{docName(d.document_id)}</Typography>
                                {d.document_number && <Typography sx={{ fontFamily: mono, fontSize: "12px", color: c.inkSoft }}>{d.document_number}</Typography>}
                            </Box>
                            <StatusPill status={d.status} />
                        </Box>
                    ))}
                </Box>
            )} */}
            {readOnlyDocs.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    <Typography sx={{ fontFamily: display, fontSize: "16px", fontWeight: 600, color: c.ink }}>
                        {"Other documents"}
                    </Typography>
                    {readOnlyDocs.map((d) => (
                        <Box key={d.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: `1px solid ${c.line}`, borderRadius: "10px" }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                {(typeof d.images === 'string' ? d.images.split(';;;;') : (Array.isArray(d.images) ? d.images : [d.images])).filter(Boolean).map((imgUrl, idx) => (
                                    <Box key={idx} component="img"
                                        src={imgUrl}
                                        alt={`${docName(d.document_id)} ${idx + 1}`}
                                        sx={{ width: 52, height: 52, borderRadius: "6px", objectFit: "cover", bgcolor: c.page }} />
                                ))}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: c.ink }}>{docName(d.document_id)}</Typography>
                                {d.document_number && <Typography sx={{ fontFamily: mono, fontSize: "12px", color: c.inkSoft }}>{d.document_number}</Typography>}
                                {isResubmitted(d) && (
                                    <Typography sx={{ fontSize: "12px", color: c.amberDeep }}>
                                        Resubmitted · awaiting review
                                    </Typography>
                                )}
                            </Box>
                            <StatusPill status={d.status} />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ minHeight: "100dvh", bgcolor: c.page, backgroundImage: "radial-gradient(rgba(18,21,25,0.07) 1px, transparent 1px)", backgroundSize: "22px 22px" }}>
                <Box sx={{ bgcolor: c.asphalt, color: "#fff", pb: 9 }}>
                    <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, sm: 4 }, pt: 5 }}>
                        <Typography sx={{ fontFamily: mono, fontSize: "12px", letterSpacing: "2px", textTransform: "uppercase", color: c.amber }}>
                            Fleet partner registration
                        </Typography>
                        <Typography component="h1" sx={{ fontFamily: display, fontWeight: 700, fontSize: { xs: "28px", md: "34px" }, letterSpacing: "-1px", mt: 1.5 }}>
                            Check your registration status
                        </Typography>
                        <Typography sx={{ fontSize: "15px", color: "rgba(255,255,255,0.6)", mt: 1 }}>
                            Enter the email you registered with.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ maxWidth: 760, mx: "auto", px: { xs: 2.5, sm: 4 }, mt: -5, pb: 8 }}>
                    <Box sx={{ bgcolor: c.surface, border: `1px solid ${c.line}`, borderRadius: "12px", p: { xs: 3, sm: 4 }, boxShadow: "0 20px 44px -28px rgba(18,21,25,0.3)" }}>
                        {!result ? lookupView : (
                            <>
                                {summaryHeader}
                                {fleet.status === 1 ? approvedView : documentsView}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} sx={{ width: "100%", borderRadius: "8px" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
};

export default FleetRegistrationStatus;